import { useCallback, useEffect, useMemo, useState } from 'react';

import { adminResetValue, HelpTypes, MiscType } from '@pharmaplan/common';

import { useAppDispatch } from './useAppDispatch';

type ISelectAllFunc<T, key extends keyof T> = {
  data: Array<T>;
  idProperty: key;
};

interface IUseTableSelectCheckbox {
  useOfEdit: false;
  page: number;
  nameOfData?: string;
  nameOfEdit?: string;
  userType: MiscType | HelpTypes | string;
  dataLength: number;
}

interface IUseTableSelectWithEdit {
  useOfEdit: true;
  // NAME OF DATA PROPERTY IN REDUCER
  nameOfData: string;
  // NAME OF UPDATE DATA PROPERTY IN REDUCER
  nameOfEdit: string;
  page: number;
  userType: MiscType | HelpTypes | string;
  dataLength: number;
}

type numberBooleanObj = { [key: number]: boolean };
type numberStringObj = { [key: number]: Array<string> | undefined };

type ISelectAll = {
  [userType: string]: numberBooleanObj;
};

export type ISelectedIds = {
  [userType: string]: numberStringObj;
};

const useTableSelectCheckbox = ({
  page,
  userType,
  nameOfData,
  nameOfEdit,
  useOfEdit,
  dataLength,
}: IUseTableSelectCheckbox | IUseTableSelectWithEdit) => {
  const dispatch = useAppDispatch();

  const [idsAlreadyThere, setIdsAlreadyThere] = useState<{
    editIds: Array<string>;
    removedIds: Array<string>;
  }>({ editIds: [], removedIds: [] });
  const [selectAll, setSelectAll] = useState<ISelectAll>({});
  const [selectedIds, setSelectedIds] = useState<ISelectedIds>({});

  const handleSelectedIds = (obj: numberStringObj) => {
    setSelectedIds((prev) =>
      ({
        ...prev,
        [userType]: { ...prev?.[userType], ...obj },
      }));
  };

  const handleSelectAll = (obj: numberBooleanObj) => {
    setSelectAll((prev) =>
      ({
        ...prev,
        [userType]: { ...prev?.[userType], ...obj },
      }));
  };

  const handleIdsAlreadyThere = (id: string) => {
    setIdsAlreadyThere((prev) =>
      ({
        editIds: prev.editIds.filter((item) =>
          item !== id),
        removedIds: [...prev.removedIds, id],
      }));
  };

  const handleRemovedIds = (id: string) => {
    setIdsAlreadyThere((prev) =>
      ({
        ...prev,
        removedIds: prev.removedIds.filter((item) =>
          item !== id),
      }));
  };

  const handlePress = (id: string) =>
    () => {
      const currentIds = selectedIds?.[userType]?.[page] ?? [];

      if (idsAlreadyThere.editIds.includes(id)) handleIdsAlreadyThere(id);
      if (idsAlreadyThere.removedIds.includes(id)) handleRemovedIds(id);

      if (currentIds?.includes(id)) {
        handleSelectedIds({ [page]: currentIds?.filter((item) =>
          item !== id) });
      } else {
        handleSelectedIds({ [page]: [...currentIds, id] });
      }
    };

  const handleAllIdsRemoval = (allIds: string[]) => {
    setIdsAlreadyThere({
      editIds: idsAlreadyThere.editIds.filter((item) =>
        !allIds.includes(item)),
      removedIds: [
        ...idsAlreadyThere.removedIds,
        ...allIds.filter((item) =>
          idsAlreadyThere.editIds.includes(item)),
      ],
    });
  };

  const handleAllIdsAdd = (allIds: string[]) => {
    setIdsAlreadyThere({
      editIds: idsAlreadyThere.editIds,
      removedIds: idsAlreadyThere.removedIds.filter(
        (item) =>
          !allIds.includes(item),
      ),
    });
  };

  const selectAllFunc = <T, key extends keyof T>({ data, idProperty }: ISelectAllFunc<T, key>) =>
    (toBeSet: boolean) => {
      let allIds: Array<string> = [];
      allIds = data.map((item) =>
        item[idProperty]) as Array<string>;
      if (toBeSet) {
        handleSelectedIds({ [page]: allIds });
        handleAllIdsAdd(allIds);
      } else {
        handleSelectedIds({ [page]: [] });
        handleAllIdsRemoval(allIds);
      }

      handleSelectAll({ [page]: toBeSet });
    };

  const handleEdit = <T, key extends keyof T>({ data, idProperty }: ISelectAllFunc<T, key>) =>
    (editIds: Array<string>) => {
      const matchingIds = data
        .map((item) =>
          item[idProperty] as string)
        .filter((id) =>
          editIds.includes(id) && !idsAlreadyThere.removedIds.includes(id));
      const currentIds = selectedIds?.[userType]?.[page] ?? [];

      handleSelectedIds({
        [page]: [...new Set([...currentIds, ...matchingIds])],
      });
    };

  useEffect(
    () =>
      () => {
        if (useOfEdit) {
          dispatch(adminResetValue([nameOfData, nameOfEdit]));
        }
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    handleSelectAll({
      [page]: selectedIds[userType]?.[page]?.length === dataLength,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds]);

  const allIds = useMemo(() => {
    let res: Array<string> = [...idsAlreadyThere.editIds];
    Object.keys(selectedIds).forEach((key) => {
      const selectedIdValues = Object.values(selectedIds?.[key] ?? {})
        .flat()
        .filter((id): id is string =>
          typeof id === 'string');

      res = [
        ...new Set([...res, ...selectedIdValues]),
      ];
      return res;
    });

    return res;
  }, [idsAlreadyThere.editIds, selectedIds]);

  const handleReset = useCallback(() => {
    setSelectAll({});
    setSelectedIds({});
  }, []);

  return {
    handlePress,
    selectAllFunc,
    selectedIds: selectedIds?.[userType]?.[page] ?? [],
    setSelectedIds,
    selectAll: !!selectAll?.[userType]?.[page],
    setSelectAll,
    handleEdit,
    allIds: allIds.filter((item) =>
      !idsAlreadyThere.removedIds.includes(item)),
    handleReset,
    handleSelectedIds,
    setIdsAlreadyThere,
    handleSelectAll,
    handleAllIdsAdd,
    handleAllIdsRemoval,
  };
};

export default useTableSelectCheckbox;
